import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon } from '../elements/Icon';
import { ICON_ARROW_RIGHT_LINE, ICON_SEND } from '../refs/Icons';

// Literal hex values — actual values come from the theme when available
const HEX_WHITE           = '#ffffff';
const HEX_LOCHMARA        = '#008dd0';
const HEX_LOCHMARA_SHADOW = '#0072a9';

type AnyProps = { theme?: { colors?: Record<string, string> } };
const t = (token: string, fallback: string) => (props: AnyProps): string =>
  props.theme?.colors?.[token] ?? fallback;

// ─── Types ────────────────────────────────────────────────────────────────────

export type AiPromptSuggestionState = 'default' | 'hover' | 'active';

export type AiPromptSuggestionProps = {
  /** The suggestion label to display */
  label: string;
  /** Called when the suggestion is clicked */
  onClick?: (label: string) => void;
  /** Disabled state */
  disabled?: boolean;
  className?: string;
};

// ─── Styled components ────────────────────────────────────────────────────────

const SuggestionButton = styled.button<{ $state: AiPromptSuggestionState }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 8px 8px 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  box-sizing: border-box;
  text-align: left;
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.33;
  transition: background-color 0.15s ease, color 0.15s ease;

  background-color: ${(props: AnyProps & { $state: AiPromptSuggestionState }) => {
    switch (props.$state) {
      case 'hover':   return t('aiPanelMainColor', HEX_LOCHMARA)(props);
      case 'active':  return t('aiPanelShadow', HEX_LOCHMARA_SHADOW)(props);
      default:        return 'transparent';
    }
  }};

  color: ${(props: AnyProps & { $state: AiPromptSuggestionState }) =>
    props.$state === 'default' ? t('aiPanelShadow', HEX_LOCHMARA_SHADOW)(props) : HEX_WHITE};

  border: 1px solid ${t('aiPanelMainColor', HEX_LOCHMARA)};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuggestionText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ArrowIcon = styled.span<{ $state: AiPromptSuggestionState }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${(props: AnyProps & { $state: AiPromptSuggestionState }) =>
    props.$state === 'default' ? t('aiPanelMainColor', HEX_LOCHMARA)(props) : HEX_WHITE};
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const AiPromptSuggestion: React.FC<AiPromptSuggestionProps> = ({
  label,
  onClick,
  disabled = false,
  className,
}) => {
  const [state, setState] = useState<AiPromptSuggestionState>('default');

  return (
    <SuggestionButton
      $state={state}
      className={className}
      disabled={disabled}
      type="button"
      onMouseEnter={() => setState('hover')}
      onMouseLeave={() => setState('default')}
      onMouseDown={() => setState('active')}
      onMouseUp={() => setState('hover')}
      onClick={() => onClick?.(label)}
      aria-label={label}
    >
      <SuggestionText>{label}</SuggestionText>
      <ArrowIcon $state={state}>
        <Icon icon={ICON_ARROW_RIGHT_LINE} size={16} />
      </ArrowIcon>
    </SuggestionButton>
  );
};

// ─── List wrapper ─────────────────────────────────────────────────────────────

export type AiPromptSuggestionsProps = {
  /** Array of suggestion strings */
  suggestions: string[];
  /** Called when a suggestion is clicked; receives the suggestion text */
  onSelect?: (label: string) => void;
  className?: string;
};

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const AiPromptSuggestions: React.FC<AiPromptSuggestionsProps> = ({
  suggestions,
  onSelect,
  className,
}) => (
  <ListWrapper className={className}>
    {suggestions.map((s) => (
      <AiPromptSuggestion key={s} label={s} onClick={onSelect} />
    ))}
  </ListWrapper>
);

export default AiPromptSuggestion;
